import axios from 'axios';
import { createAsyncThunk, createSlice, EntityState } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import {
  FeedbackState,
  IFeedbackQuestion,
  IFeedbackQuestionsResponse,
  IMeetingFeedback,
  defaultFeedbackEntity,
} from 'app/shared/model/meeting-feedback.model';

const initialState: FeedbackState = {
  loading: false,
  updating: false,

  errorMessage: null,
  questions: [],
  questionsResponse: null,

  entity: { ...defaultFeedbackEntity },
  feedbackList: [],

  updateSuccess: false,
  alreadySubmitted: false,
};

const questionApi = '/api/meeting/questions';
const saveApi = '/api/saveFeedback';
const feedBackViewApi = '/api/meeting/feedback';
export const fetchFeedbackQuestions = createAsyncThunk(
  'meetingFeedback/fetch_questions',
  async ({ mentorId, meetingId }: { mentorId: string; meetingId: number }) => {
    const response = await axios.get<IFeedbackQuestionsResponse>(questionApi, {
      params: { mentorId, meetingId },
    });

    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const fetchFeedback = createAsyncThunk(
  'meetingFeedback/fetch_feedback',
  async ({ menteeId, meetingId }: { menteeId: string; meetingId: number }) => {
    const response = await axios.get(feedBackViewApi, {
      params: { menteeId, meetingId },
    });

    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const saveMeetingFeedback = createAsyncThunk(
  'meetingFeedback/save',
  async (data: IMeetingFeedback) => {
    const response = await axios.put<IMeetingFeedback>(saveApi, data);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const MeetingFeedbackSlice = createSlice({
  name: 'meetingFeedback',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFeedbackQuestions.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(fetchFeedbackQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questionsResponse = action.payload;

        state.questions =
          action.payload?.questions?.map(q => ({
            id: q.questionId,
            question: q.question,
            isShareFeedBack: q.isShareFeedBack,
            feedBack: q.feedBack,
          })) || [];
      })
      .addCase(fetchFeedbackQuestions.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Error fetching questions';
      })
      .addCase(fetchFeedback.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(fetchFeedback.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || [];

        state.feedbackList = data;
      })
      .addCase(fetchFeedback.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? 'Error fetching feedback';
      })

      .addCase(saveMeetingFeedback.pending, state => {
        state.updating = true;
        state.updateSuccess = false;
        state.errorMessage = null;
      })
      .addCase(saveMeetingFeedback.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = action.payload;
        state.alreadySubmitted = true;
      })
      .addCase(saveMeetingFeedback.rejected, (state, action) => {
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.error.message ?? 'Error saving feedback';
      });
  },
});

export const { reset } = MeetingFeedbackSlice.actions;
export default MeetingFeedbackSlice.reducer;
